import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { StyledTextField } from '@modules/ui-components';
import { AD_CREATIVE_BATCH } from '../../selectors';
import type {
  AdCreativeApiClient,
  AdCreativeApprovedAudioCandidateView,
} from '../../services/AdCreativeApiClient';

type ApprovedAudioCandidateClient = Pick<
  AdCreativeApiClient,
  | 'listItemVideoAudioCandidates'
  | 'uploadItemVideoAudioCandidate'
  | 'loadItemVideoAudioCandidatePlayback'
  | 'reviewItemVideoAudioCandidate'
>;

export interface ApprovedAudioCandidatePanelProps {
  projectId: string;
  itemId: string;
  slotKey: string;
  targetLocale: string;
  captionArtifactId?: string;
  voiceRightsEvidenceId?: string;
  approvedVoiceId?: string;
  client: ApprovedAudioCandidateClient;
  selectedArtifactId?: string;
  disabled?: boolean;
  onSelectArtifact: (artifactId?: string) => void;
}

interface CandidateConfirmations {
  targetLanguageConfirmed: boolean;
  approvedVoiceConfirmed: boolean;
  captionTimingWithin250MsConfirmed: boolean;
}

const EMPTY_CONFIRMATIONS: CandidateConfirmations = {
  targetLanguageConfirmed: false,
  approvedVoiceConfirmed: false,
  captionTimingWithin250MsConfirmed: false,
};

const PAGE_SIZE = 100;

/**
 * Human-review surface for immutable reviewer-supplied localized audio.
 *
 * The browser never receives a storage locator or supplies artifact authority.
 * It uploads one bounded file, plays exact server-verified bytes, records the
 * four explicit attestations, and selects only the artifact ID returned by the
 * server after immutable promotion.
 */
export function ApprovedAudioCandidatePanel({
  projectId,
  itemId,
  slotKey,
  targetLocale,
  captionArtifactId,
  voiceRightsEvidenceId,
  approvedVoiceId,
  client,
  selectedArtifactId,
  disabled = false,
  onSelectArtifact,
}: ApprovedAudioCandidatePanelProps): React.ReactElement {
  const [candidates, setCandidates] = useState<AdCreativeApprovedAudioCandidateView[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [busyCandidateId, setBusyCandidateId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [playback, setPlayback] = useState<{ candidateId: string; url: string } | null>(null);
  const [playbackComplete, setPlaybackComplete] = useState<Set<string>>(() => new Set());
  const [confirmations, setConfirmations] = useState<Record<string, CandidateConfirmations>>({});
  const [rejectionReasons, setRejectionReasons] = useState<Record<string, string>>({});
  const identity = `${projectId}:${itemId}:${slotKey}:${targetLocale}:${captionArtifactId ?? ''}:${voiceRightsEvidenceId ?? ''}:${approvedVoiceId ?? ''}`;
  const identityRef = useRef(identity);
  const playbackRef = useRef<{ candidateId: string; url: string } | null>(null);
  identityRef.current = identity;
  playbackRef.current = playback;

  const apiAvailable = typeof client.listItemVideoAudioCandidates === 'function'
    && typeof client.uploadItemVideoAudioCandidate === 'function'
    && typeof client.loadItemVideoAudioCandidatePlayback === 'function'
    && typeof client.reviewItemVideoAudioCandidate === 'function';

  const releasePlayback = useCallback((): void => {
    const current = playbackRef.current;
    if (current) URL.revokeObjectURL(current.url);
    playbackRef.current = null;
    setPlayback(null);
  }, []);

  const mergeCandidate = useCallback((candidate: AdCreativeApprovedAudioCandidateView): void => {
    setCandidates((current) => {
      const withoutCandidate = current.filter((entry) => entry.evidenceId !== candidate.evidenceId);
      return [candidate, ...withoutCandidate];
    });
  }, []);

  const loadCandidates = useCallback(async (options: {
    cursor?: string;
    append?: boolean;
    expectedIdentity?: string;
  } = {}): Promise<void> => {
    if (!apiAvailable) return;
    const expectedIdentity = options.expectedIdentity ?? identity;
    const response = await client.listItemVideoAudioCandidates(projectId, itemId, {
      limit: PAGE_SIZE,
      ...(options.cursor ? { cursor: options.cursor } : {}),
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Approved-audio candidates could not be loaded.');
    }
    if (identityRef.current !== expectedIdentity) return;
    setCandidates((current) => options.append
      ? mergeCandidatePages(current, response.data?.candidates ?? [])
      : response.data?.candidates ?? []);
    setHasMore(response.data.hasMore);
    setNextCursor(response.data.nextCursor);
  }, [apiAvailable, client, identity, itemId, projectId]);

  useEffect(() => {
    let active = true;
    releasePlayback();
    setCandidates([]);
    setNextCursor(undefined);
    setHasMore(false);
    setPlaybackComplete(new Set());
    setConfirmations({});
    setRejectionReasons({});
    setError(null);
    setStatus(null);
    setLoading(apiAvailable);
    if (!apiAvailable) return () => { active = false; };
    void loadCandidates({ expectedIdentity: identity })
      .catch((nextError) => {
        if (active && identityRef.current === identity) setError(errorMessage(nextError));
      })
      .finally(() => {
        if (active && identityRef.current === identity) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [apiAvailable, identity, loadCandidates, releasePlayback]);

  useEffect(() => () => {
    const current = playbackRef.current;
    if (current) URL.revokeObjectURL(current.url);
  }, []);

  const exactCandidates = useMemo(() => candidates.filter((candidate) => (
    candidate.creativeItemId === itemId
    && candidate.slotKey === slotKey
    && candidate.targetLocale === targetLocale
    && candidate.captionArtifactId === captionArtifactId
    && candidate.voiceRightsEvidenceId === voiceRightsEvidenceId
    && candidate.approvedVoiceId === approvedVoiceId
  )), [
    approvedVoiceId,
    candidates,
    captionArtifactId,
    itemId,
    slotKey,
    targetLocale,
    voiceRightsEvidenceId,
  ]);
  const hiddenHistoricalCount = candidates.length - exactCandidates.length;
  const uploadReady = Boolean(captionArtifactId && voiceRightsEvidenceId && approvedVoiceId);

  const upload = async (file: File): Promise<void> => {
    if (!uploadReady || !captionArtifactId || !voiceRightsEvidenceId) return;
    const expectedIdentity = identity;
    setUploading(true);
    setError(null);
    setStatus(null);
    try {
      const response = await client.uploadItemVideoAudioCandidate(projectId, itemId, {
        targetLocale,
        slotKey,
        captionArtifactId,
        voiceRightsEvidenceId,
      }, file);
      if (!response.success || !response.data?.candidate) {
        throw new Error(response.error || 'The localized audio could not be uploaded.');
      }
      if (identityRef.current !== expectedIdentity) return;
      mergeCandidate(response.data.candidate);
      setStatus(response.data.reused
        ? 'The existing immutable upload was reused. Listen to it before approval.'
        : 'Immutable audio uploaded. Listen to the complete exact track before approval.');
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) setError(errorMessage(nextError));
    } finally {
      if (identityRef.current === expectedIdentity) setUploading(false);
    }
  };

  const loadPlayback = async (candidateId: string): Promise<void> => {
    const expectedIdentity = identity;
    setBusyCandidateId(candidateId);
    setError(null);
    setStatus(null);
    try {
      const response = await client.loadItemVideoAudioCandidatePlayback(projectId, itemId, candidateId);
      if (!response.success || !response.blob) {
        throw new Error(response.error || 'The exact audio bytes could not be loaded.');
      }
      if (identityRef.current !== expectedIdentity) return;
      releasePlayback();
      const url = URL.createObjectURL(response.blob);
      const nextPlayback = { candidateId, url };
      playbackRef.current = nextPlayback;
      setPlayback(nextPlayback);
      setPlaybackComplete((current) => {
        const next = new Set(current);
        next.delete(candidateId);
        return next;
      });
      setStatus('Exact server-verified audio loaded. Listen through the end to unlock approval.');
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) setError(errorMessage(nextError));
    } finally {
      if (identityRef.current === expectedIdentity) setBusyCandidateId(null);
    }
  };

  const review = async (candidate: AdCreativeApprovedAudioCandidateView, decision: 'approve' | 'reject'): Promise<void> => {
    const expectedIdentity = identity;
    const candidateId = candidate.evidenceId;
    setBusyCandidateId(candidateId);
    setError(null);
    setStatus(null);
    try {
      const candidateConfirmations = confirmations[candidateId] ?? EMPTY_CONFIRMATIONS;
      const response = await client.reviewItemVideoAudioCandidate(
        projectId,
        itemId,
        candidateId,
        decision === 'approve'
          ? {
              decision: 'approve',
              confirmations: {
                exactPlaybackConfirmed: true,
                targetLanguageConfirmed: true,
                approvedVoiceConfirmed: true,
                captionTimingWithin250MsConfirmed: true,
              },
            }
          : {
              decision: 'reject',
              rejectionReason: (rejectionReasons[candidateId] ?? '').trim(),
            },
      );
      if (!response.success || !response.data?.candidate) {
        throw new Error(response.error || `The audio candidate could not be ${decision === 'approve' ? 'approved' : 'rejected'}.`);
      }
      if (identityRef.current !== expectedIdentity) return;
      mergeCandidate(response.data.candidate);
      if (decision === 'approve') {
        const artifactId = response.data.artifact?.artifactId
          ?? (response.data.candidate.review.status === 'approved'
            ? response.data.candidate.review.artifactId
            : undefined);
        if (artifactId) {
          onSelectArtifact(artifactId);
          setStatus(response.data.reusedArtifact
            ? 'Existing approved audio verified and selected.'
            : 'Audio approved, promoted to an immutable artifact, and selected.');
        } else {
          setStatus('Approval is recorded, but artifact promotion is not complete. Replay the exact track and finalize approval.');
        }
      } else {
        setStatus('Audio rejected. The immutable candidate remains in review history.');
      }
      setConfirmations((current) => ({ ...current, [candidateId]: candidateConfirmations }));
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) setError(errorMessage(nextError));
    } finally {
      if (identityRef.current === expectedIdentity) setBusyCandidateId(null);
    }
  };

  const loadMore = async (): Promise<void> => {
    if (!nextCursor) return;
    const expectedIdentity = identity;
    setLoadingMore(true);
    setError(null);
    try {
      await loadCandidates({ cursor: nextCursor, append: true, expectedIdentity });
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) setError(errorMessage(nextError));
    } finally {
      if (identityRef.current === expectedIdentity) setLoadingMore(false);
    }
  };

  return (
    <Box
      component="section"
      aria-labelledby="approved-audio-candidates-heading"
      data-testid={AD_CREATIVE_BATCH.drawerApprovedAudioPanel}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography id="approved-audio-candidates-heading" variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
            Approved localized audio
          </Typography>
          {selectedArtifactId && <Chip size="small" color="success" label="Audio selected" />}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Upload WAV or M4A/AAC made for the exact translated captions. Segmently stores an immutable copy; nothing in Facebook or Meta is changed.
        </Typography>

        {!apiAvailable && (
          <Alert severity="info">Approved-audio review is unavailable in this build.</Alert>
        )}
        {!captionArtifactId && (
          <Alert severity="info">Create the caption-only localized video first. Its exact translated caption artifact is required before audio upload.</Alert>
        )}
        {captionArtifactId && (!voiceRightsEvidenceId || !approvedVoiceId) && (
          <Alert severity="info">Select an active voice-rights approval before uploading localized audio.</Alert>
        )}

        {apiAvailable && loading && (
          <Stack direction="row" spacing={1} alignItems="center" role="status">
            <CircularProgress size={14} />
            <Typography variant="caption">Loading immutable audio review history…</Typography>
          </Stack>
        )}

        {apiAvailable && uploadReady && (
          <Button
            component="label"
            variant="outlined"
            size="small"
            disabled={disabled || uploading}
            data-testid={AD_CREATIVE_BATCH.drawerApprovedAudioUpload}
          >
            {uploading ? 'Uploading and inspecting…' : 'Upload localized audio'}
            <input
              hidden
              type="file"
              accept=".wav,.m4a,audio/wav,audio/mp4"
              onChange={(event) => {
                const input = event.currentTarget;
                const file = input.files?.[0];
                input.value = '';
                if (file) void upload(file);
              }}
            />
          </Button>
        )}

        {hiddenHistoricalCount > 0 && (
          <Typography variant="caption" color="text.secondary">
            {hiddenHistoricalCount} historical {hiddenHistoricalCount === 1 ? 'candidate is' : 'candidates are'} hidden because the caption artifact, voice rights, slot, or language no longer matches.
          </Typography>
        )}

        {!loading && apiAvailable && exactCandidates.length === 0 && uploadReady && (
          <Alert severity="info">No audio candidate matches the exact current captions, approved voice, and target language.</Alert>
        )}

        {exactCandidates.map((candidate) => {
          const candidateConfirmations = confirmations[candidate.evidenceId] ?? EMPTY_CONFIRMATIONS;
          const heardComplete = playbackComplete.has(candidate.evidenceId);
          const approveReady = heardComplete
            && candidateConfirmations.targetLanguageConfirmed
            && candidateConfirmations.approvedVoiceConfirmed
            && candidateConfirmations.captionTimingWithin250MsConfirmed;
          const rejectionReason = rejectionReasons[candidate.evidenceId] ?? '';
          const rejectReady = utf8Length(rejectionReason.trim()) >= 3
            && utf8Length(rejectionReason.trim()) <= 2_000;
          const artifactId = candidate.review.status === 'approved'
            ? candidate.review.artifactId
            : undefined;
          const needsApprovalAction = candidate.review.status === 'required'
            || (candidate.review.status === 'approved' && !artifactId);
          const isSelected = Boolean(artifactId && artifactId === selectedArtifactId);
          const candidateBusy = busyCandidateId === candidate.evidenceId;
          return (
            <Box
              key={candidate.evidenceId}
              data-testid={AD_CREATIVE_BATCH.drawerApprovedAudioCard(candidate.evidenceId)}
              sx={{ border: 1, borderColor: isSelected ? 'success.main' : 'divider', borderRadius: 1, p: 1 }}
            >
              <Stack spacing={0.75}>
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" sx={{ fontWeight: 700, flex: 1 }}>
                    {candidate.approvedVoiceId}
                  </Typography>
                  <Chip
                    size="small"
                    color={candidate.review.status === 'approved'
                      ? 'success'
                      : candidate.review.status === 'rejected' ? 'default' : 'warning'}
                    label={candidate.review.status === 'required' ? 'Review required' : candidate.review.status}
                  />
                  {isSelected && <Chip size="small" color="success" variant="outlined" label="Selected" />}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {formatDuration(candidate.audio.durationMs)} · {candidate.audio.codec} · {formatBytes(candidate.audio.byteSize)} · {formatDate(candidate.createdAt)}
                </Typography>

                <Button
                  size="small"
                  variant="text"
                  disabled={disabled || candidateBusy}
                  data-testid={AD_CREATIVE_BATCH.drawerApprovedAudioPlayback(candidate.evidenceId)}
                  onClick={() => void loadPlayback(candidate.evidenceId)}
                >
                  {candidateBusy ? 'Working…' : 'Load exact audio'}
                </Button>
                {playback?.candidateId === candidate.evidenceId && (
                  <audio
                    controls
                    preload="metadata"
                    src={playback.url}
                    aria-label={`Exact localized audio · ${candidate.approvedVoiceId}`}
                    controlsList="nodownload"
                    onEnded={() => {
                      setPlaybackComplete((current) => new Set(current).add(candidate.evidenceId));
                      setStatus('Complete exact playback confirmed. Finish the remaining review checks.');
                    }}
                  />
                )}

                {needsApprovalAction && (
                  <Stack spacing={0.25}>
                    <FormControlLabel
                      control={<Checkbox size="small" checked={heardComplete} disabled />}
                      label="I listened to the complete exact track"
                      sx={{ '& .MuiFormControlLabel-label': { typography: 'caption' } }}
                    />
                    {([
                      ['targetLanguageConfirmed', `Spoken language is ${targetLocale}`],
                      ['approvedVoiceConfirmed', `Voice identity matches ${candidate.approvedVoiceId}`],
                      ['captionTimingWithin250MsConfirmed', 'Audio follows the approved captions within 250 ms'],
                    ] as const).map(([key, label]) => (
                      <FormControlLabel
                        key={key}
                        control={(
                          <Checkbox
                            size="small"
                            checked={candidateConfirmations[key]}
                            disabled={disabled || candidateBusy}
                            data-testid={AD_CREATIVE_BATCH.drawerApprovedAudioConfirmation(candidate.evidenceId, key)}
                            onChange={(event) => setConfirmations((current) => ({
                              ...current,
                              [candidate.evidenceId]: {
                                ...(current[candidate.evidenceId] ?? EMPTY_CONFIRMATIONS),
                                [key]: event.target.checked,
                              },
                            }))}
                          />
                        )}
                        label={label}
                        sx={{ '& .MuiFormControlLabel-label': { typography: 'caption' } }}
                      />
                    ))}
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={disabled || candidateBusy || !approveReady}
                        data-testid={AD_CREATIVE_BATCH.drawerApprovedAudioApprove(candidate.evidenceId)}
                        onClick={() => void review(candidate, 'approve')}
                      >
                        {candidate.review.status === 'approved' ? 'Finalize and use' : 'Approve and use'}
                      </Button>
                    </Stack>
                    {candidate.review.status === 'required' && (
                      <>
                        <StyledTextField
                          fullWidth
                          multiline
                          minRows={2}
                          size="small"
                          label="Rejection reason"
                          value={rejectionReason}
                          disabled={disabled || candidateBusy}
                          onChange={(value) => setRejectionReasons((current) => ({
                            ...current,
                            [candidate.evidenceId]: String(value),
                          }))}
                        />
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          disabled={disabled || candidateBusy || !rejectReady}
                          data-testid={AD_CREATIVE_BATCH.drawerApprovedAudioReject(candidate.evidenceId)}
                          onClick={() => void review(candidate, 'reject')}
                        >
                          Reject audio
                        </Button>
                      </>
                    )}
                  </Stack>
                )}

                {candidate.review.status === 'approved' && artifactId && (
                  <Button
                    size="small"
                    variant={isSelected ? 'outlined' : 'contained'}
                    disabled={disabled || isSelected}
                    data-testid={AD_CREATIVE_BATCH.drawerApprovedAudioSelect(candidate.evidenceId)}
                    onClick={() => onSelectArtifact(artifactId)}
                  >
                    {isSelected ? 'Using this audio' : 'Use this approved audio'}
                  </Button>
                )}
                {candidate.review.status === 'approved' && !artifactId && (
                  <Alert severity="warning">
                    Approval exists, but immutable promotion is incomplete. Reload the exact audio and repeat the confirmations to finalize safely.
                  </Alert>
                )}
                {candidate.review.status === 'rejected' && (
                  <Alert severity="info">Rejected: {candidate.review.rejectionReason}</Alert>
                )}
              </Stack>
            </Box>
          );
        })}

        {hasMore && nextCursor && (
          <Button
            size="small"
            variant="text"
            disabled={disabled || loadingMore}
            data-testid={AD_CREATIVE_BATCH.drawerApprovedAudioLoadMore}
            onClick={() => void loadMore()}
          >
            {loadingMore ? 'Loading…' : 'Load older candidates'}
          </Button>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {status && <Alert severity="success">{status}</Alert>}
      </Stack>
    </Box>
  );
}

function mergeCandidatePages(
  current: AdCreativeApprovedAudioCandidateView[],
  next: AdCreativeApprovedAudioCandidateView[],
): AdCreativeApprovedAudioCandidateView[] {
  const byId = new Map(current.map((candidate) => [candidate.evidenceId, candidate]));
  next.forEach((candidate) => byId.set(candidate.evidenceId, candidate));
  return [...byId.values()];
}

function formatDuration(durationMs: number): string {
  return `${(durationMs / 1_000).toFixed(durationMs % 1_000 === 0 ? 0 : 1)}s`;
}

function formatBytes(byteSize: number): string {
  if (byteSize < 1_024) return `${byteSize} B`;
  if (byteSize < 1_024 * 1_024) return `${(byteSize / 1_024).toFixed(1)} KB`;
  return `${(byteSize / (1_024 * 1_024)).toFixed(1)} MB`;
}

function formatDate(value: string): string {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? new Date(timestamp).toLocaleString() : value;
}

function utf8Length(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
